import { countPossibleQuestions } from "./possibleQuestionCount";

const NON_PROGRESS_QUESTION_TYPES = new Set([
  "completion",
  "treatment_screening_intro",
]);

const questionIdentityCandidates = (question) => (
  [question?.release_node_id, question?.id, question?.source_id]
    .filter((value) => value !== undefined && value !== null && value !== "")
    .map(String)
);

export const questionProgressIdentity = (question) => (
  questionIdentityCandidates(question)[0] || ""
);

const isProgressQuestion = (question) => Boolean(
  question
  && question.id
  && question.runtime_only !== true
  && !NON_PROGRESS_QUESTION_TYPES.has(question.question_type),
);

const sameQuestion = (left, right) => {
  const leftIdentities = questionIdentityCandidates(left);
  const rightIdentities = questionIdentityCandidates(right);
  const leftStrongIdentities = leftIdentities.slice(0, 2);
  const rightStrongIdentities = new Set(rightIdentities.slice(0, 2));
  const hasStrongIdentity = leftStrongIdentities.length > 0
    && rightStrongIdentities.size > 0;
  const identitiesToCompare = hasStrongIdentity
    ? leftStrongIdentities
    : leftIdentities;
  const rightIdentitiesToCompare = hasStrongIdentity
    ? rightStrongIdentities
    : new Set(rightIdentities);
  return identitiesToCompare.some((identity) => (
    rightIdentitiesToCompare.has(identity)
  ));
};

const validPositiveInteger = (value) => Number.isInteger(value) && value > 0;

const runtimeTotal = (progress) => {
  if (progress?.mode === "exact" && validPositiveInteger(progress.total)) {
    return progress.total;
  }
  return null;
};

export const possibleTreatmentQuestionCount = ({ questionnaire, responses, authorityState }) => (
  countPossibleQuestions({ questionnaire, responses, authorityState, isProgressQuestion })
);

export const createOverallQuestionProgressHistory = (releaseKey = "") => ({
  releaseKey,
  entries: [],
  cursor: -1,
  restoredCursor: -1,
});

/**
 * Record the progress checkpoint for the currently rendered question.
 *
 * The numerator is a traversal count: every newly visited question adds one.
 * The denominator is the current possible maximum, stored with the checkpoint
 * so navigating backward restores the denominator seen at that question.
 */
export const updateOverallQuestionProgress = ({
  history,
  releaseKey = "",
  allQuestions,
  currentQuestion,
  runtimeProgress,
  possibleTotal = null,
  branchKey = "",
  completed = false,
  terminalQuestion = false,
}) => {
  const releasedQuestions = Array.isArray(allQuestions)
    ? allQuestions.filter(isProgressQuestion)
    : [];
  const fallbackTotal = releasedQuestions.length;
  if (!fallbackTotal || !currentQuestion) {
    return {
      history: createOverallQuestionProgressHistory(releaseKey),
      progress: null,
    };
  }

  const currentIdentity = questionProgressIdentity(currentQuestion);
  if (!currentIdentity) {
    return {
      history: createOverallQuestionProgressHistory(releaseKey),
      progress: null,
    };
  }

  const activeHistory = history?.releaseKey === releaseKey
    ? history
    : createOverallQuestionProgressHistory(releaseKey);
  const currentEntry = activeHistory.entries[activeHistory.cursor];

  if (currentEntry?.identity === currentIdentity) {
    const branchChanged = (
      currentEntry.branchKey !== ""
      && branchKey !== ""
      && currentEntry.branchKey !== branchKey
    );
    const resolvedTotal = validPositiveInteger(possibleTotal)
      ? possibleTotal
      : runtimeTotal(runtimeProgress);
    const shouldRefreshTotal = (
      !branchChanged
      &&
      activeHistory.restoredCursor !== activeHistory.cursor
      && resolvedTotal !== null
      && resolvedTotal !== currentEntry.progress.total
    );
    if (!branchChanged && !completed && !terminalQuestion && !shouldRefreshTotal) {
      return { history: activeHistory, progress: currentEntry.progress };
    }

    const total = terminalQuestion && !completed
      ? currentEntry.progress.current
      : shouldRefreshTotal && resolvedTotal !== null
        ? Math.max(resolvedTotal, currentEntry.progress.current)
        : currentEntry.progress.total;

    const updatedEntry = {
      ...currentEntry,
      branchKey,
      progress: completed
        ? {
            mode: "exact",
            current: total,
            total,
          }
        : {
            mode: "exact",
            current: currentEntry.progress.current,
            total,
          },
    };
    const entries = [
      ...activeHistory.entries.slice(0, activeHistory.cursor),
      updatedEntry,
    ];
    return {
      history: {
        ...activeHistory,
        entries,
        cursor: entries.length - 1,
        restoredCursor: -1,
      },
      progress: updatedEntry.progress,
    };
  }

  const existingIndex = activeHistory.entries.findIndex((entry) => (
    entry.identity === currentIdentity
  ));
  if (existingIndex >= 0) {
    const entry = activeHistory.entries[existingIndex];
    return {
      history: { ...activeHistory, cursor: existingIndex, restoredCursor: existingIndex },
      progress: completed
        ? {
            mode: "exact",
            current: entry.progress.total,
            total: entry.progress.total,
          }
        : entry.progress,
    };
  }

  const releasedIndex = releasedQuestions.findIndex((question) => (
    sameQuestion(question, currentQuestion)
  ));
  if (releasedIndex < 0) {
    return {
      history: activeHistory,
      progress: null,
    };
  }
  const previousProgress = currentEntry?.progress;
  const current = previousProgress
    ? previousProgress.current + 1
    : (validPositiveInteger(runtimeProgress?.current)
      ? runtimeProgress.current
      : releasedIndex + 1);
  const total = terminalQuestion && !completed
    ? current
    : Math.max(
        (validPositiveInteger(possibleTotal) ? possibleTotal : runtimeTotal(runtimeProgress))
          || fallbackTotal,
        current,
      );
  const progress = {
    mode: "exact",
    current: completed ? total : current,
    total,
  };
  const entriesBeforeCursor = activeHistory.cursor >= 0
    ? activeHistory.entries.slice(0, activeHistory.cursor + 1)
    : [];
  const entries = [
    ...entriesBeforeCursor,
    { identity: currentIdentity, branchKey, progress },
  ];

  return {
    history: {
      ...activeHistory,
      entries,
      cursor: entries.length - 1,
      restoredCursor: -1,
    },
    progress,
  };
};

export { isProgressQuestion, questionIdentityCandidates };
